/**
 * Onboarding Application Hooks
 *
 * TanStack Query hooks for onboarding application management.
 * Provides caching, optimistic updates, and real-time subscriptions.
 *
 * Usage:
 * ```typescript
 * // Get current user's application
 * const { data: application, isLoading } = useApplication(user.uid);
 *
 * // Save draft
 * const saveDraft = useSaveDraft();
 * saveDraft.mutate({ id: application.id, data: formData, step: 1 });
 *
 * // Real-time updates
 * useApplicationSubscription(application?.id);
 * ```
 */

import {
    useMutation,
    useQuery,
    useQueryClient,
    type QueryClient,
    type UseQueryOptions
} from '@tanstack/react-query';
import type {
    IOnboardingApplication,
    RepositoryResult
} from '@order-up/models';
import type { IMerchant } from '@order-up/models';
import { createLogger } from '../utils/logger';
import {
    getOnboardingService,
    type CreateApplicationInput,
    type UpdateApplicationInput
} from '../services/onboarding';
import { authKeys } from './useAuth';
import { userKeys } from './useUsers';

const logger = createLogger('useOnboarding');

/**
 * Snapshot returned by `applyOptimistic` — a frozen copy of every cached
 * application entry *before* the optimistic mutation was applied. Passed
 * as the mutation context so `onError` can roll back if the server
 * rejects the change.
 */
type ApplicationCacheSnapshot = ReadonlyArray<
    readonly [readonly unknown[], RepositoryResult<IOnboardingApplication> | undefined]
>;

/**
 * Apply an optimistic patch to every cached onboarding application
 * matching `id`. Returns a snapshot so `onError` can revert.
 *
 * React Query v5 pattern — see:
 * https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates
 *
 * Why this shape:
 *   - We cancel in-flight fetches for the applications subtree so a
 *     pending GET can't clobber our optimistic value.
 *   - We walk every cached entry (both `application(id)` and
 *     `userApplication(uid)` keys can hold the same record) and patch
 *     each in place.
 *   - The returned snapshot is a plain array of [key, prev-value] tuples;
 *     rollback is a straight assignment back.
 */
async function applyOptimistic(
    queryClient: QueryClient,
    id: string,
    patch: (current: IOnboardingApplication) => IOnboardingApplication,
): Promise<ApplicationCacheSnapshot> {
    await queryClient.cancelQueries({ queryKey: onboardingKeys.applications() });

    const snapshot = queryClient.getQueriesData<RepositoryResult<IOnboardingApplication>>({
        queryKey: onboardingKeys.applications(),
    });

    snapshot.forEach(([key, value]) => {
        if (value?.success && value.data?.id === id) {
            const optimistic: RepositoryResult<IOnboardingApplication> = {
                success: true,
                data: patch(value.data),
            };
            queryClient.setQueryData(key, optimistic);
        }
    });

    return snapshot;
}

/** Restore a previously-captured cache snapshot after a failed optimistic update. */
function rollbackOptimistic(
    queryClient: QueryClient,
    snapshot: ApplicationCacheSnapshot | undefined,
): void {
    if (!snapshot) return;
    snapshot.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
    });
}

/**
 * Polling cadence for client-onboarding applications stuck in `submitted` or
 * `pending_review`. With the synchronous-submit pipeline (W6), the happy
 * path returns `provisioned` directly — there is no waiting state. The
 * only way an application stays in `submitted` for any meaningful amount
 * of time is a partial-failure mid-provision, in which case ops manually
 * recovers via `POST /api/v1/admin/onboarding/:id/approve`.
 *
 * To keep that recovery surface visible to the client without burning
 * battery in the happy path, we only kick polling on once the row has
 * been in `submitted` for at least {@link POLL_GRACE_MS}. Within that
 * grace window the client trusts the synchronous response. Past it, the
 * row is genuinely stuck and worth polling.
 *
 * React Query automatically pauses polling when the tab is hidden and
 * refetches on focus, so the recovery covers the "user tabs back after
 * an admin approve" case without a persistent WebSocket.
 */
const POLL_INTERVAL_MS = 5000;
const POLL_GRACE_MS = 60_000;

function shouldPoll(
    result: RepositoryResult<IOnboardingApplication> | undefined,
): number | false {
    if (!result?.success || !result.data) return false;
    const status = result.data.status;
    if (status !== 'submitted' && status !== 'pending_review') return false;

    const submittedAtRaw = result.data.submittedAt;
    if (!submittedAtRaw) return POLL_INTERVAL_MS; // no timestamp → defensive: poll
    const submittedAt = new Date(submittedAtRaw).getTime();
    if (Number.isNaN(submittedAt)) return POLL_INTERVAL_MS;
    if (Date.now() - submittedAt < POLL_GRACE_MS) return false;
    return POLL_INTERVAL_MS;
}

// ============================================================================
// Query Key Factory
// ============================================================================

export const onboardingKeys = {
    all: ['onboarding'] as const,
    applications: () => [...onboardingKeys.all, 'applications'] as const,
    application: (id: string) => [...onboardingKeys.applications(), id] as const,
    userApplication: (uid: string) => [...onboardingKeys.applications(), 'user', uid] as const
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Get application by ID
 */
export function useApplicationById(
    id: string | undefined,
    options?: Omit<UseQueryOptions<RepositoryResult<IOnboardingApplication>>, 'queryKey' | 'queryFn'>
) {
    const service = getOnboardingService();

    return useQuery({
        queryKey: onboardingKeys.application(id ?? ''),
        queryFn: async () => {
            if (!id) {
                return { success: false, error: 'No application ID provided' };
            }
            return service.getApplication(id);
        },
        enabled: !!id,
        staleTime: 1000 * 60 * 5, // 5 minutes
        ...options
    });
}

/**
 * Get application for current user.
 *
 * Polls every 5s while the application is in a non-terminal submitted state
 * (waiting on underwriting) so the UI transitions from "under review" to
 * "approved" / "declined" without a page reload. React Query pauses polling
 * while the tab is hidden and refetches on focus.
 */
export function useApplication(
    firebaseUid: string | undefined,
    options?: Omit<UseQueryOptions<RepositoryResult<IOnboardingApplication>>, 'queryKey' | 'queryFn'>
) {
    const service = getOnboardingService();

    return useQuery({
        queryKey: onboardingKeys.userApplication(firebaseUid ?? ''),
        queryFn: async () => {
            if (!firebaseUid) {
                return { success: false, error: 'No user ID provided' };
            }
            return service.getApplicationByUser(firebaseUid);
        },
        enabled: !!firebaseUid,
        staleTime: 1000 * 60 * 5, // 5 minutes
        // The draft only changes via the user's own auto-save mutations
        // (which write the cache directly via setQueryData) or via the
        // explicit `shouldPoll` refetch when status is in a non-terminal
        // submitted state. React Query's default `refetchOnWindowFocus`
        // would fire a wasteful `/api/v1/onboarding/me` round-trip every
        // time the user tabs back from email — compounding the slow
        // first-paint perception. Mirror `useAuth`'s same opt-out
        // (`packages/client/src/hooks/useAuth.ts:41`).
        refetchOnWindowFocus: false,
        refetchInterval: (query) => shouldPoll(query.state.data),
        refetchIntervalInBackground: false,
        ...options
    });
}

/**
 * Create or get application for user
 */
export function useCreateOrGetApplication() {
    const queryClient = useQueryClient();
    const service = getOnboardingService();

    return useMutation({
        mutationFn: async (input: CreateApplicationInput) => {
            logger.debug('Creating or getting application', { uid: input.firebaseUid });
            return service.createOrGetApplication(input);
        },
        onSuccess: (result, variables) => {
            if (result.success && result.data) {
                // Update both query caches
                queryClient.setQueryData(
                    onboardingKeys.application(result.data.id),
                    result
                );
                queryClient.setQueryData(
                    onboardingKeys.userApplication(variables.firebaseUid),
                    result
                );
            }
        }
    });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Save draft mutation.
 *
 * Optimistic: as soon as `mutate(...)` is called the cache is patched
 * with the merged `applicationData` and the new `currentStep`, so any
 * component reading from React Query sees the change instantly. When
 * the server confirms, the real response replaces the optimistic value.
 * If the server rejects, `onError` restores the pre-mutation snapshot.
 */
export function useSaveDraft() {
    const queryClient = useQueryClient();
    const service = getOnboardingService();

    return useMutation<
        RepositoryResult<IOnboardingApplication>,
        Error,
        { id: string; data: Partial<IMerchant>; step: number },
        ApplicationCacheSnapshot
    >({
        mutationFn: async ({ id, data, step }) => {
            logger.debug('Saving draft', { id, step });
            return service.saveDraft(id, data, step);
        },
        onMutate: ({ id, data, step }) =>
            applyOptimistic(queryClient, id, (current) => ({
                ...current,
                applicationData: {
                    ...current.applicationData,
                    ...data,
                } as IMerchant,
                currentStep: step,
                updatedAt: new Date().toISOString(),
            })),
        onError: (_err, _vars, snapshot) => {
            rollbackOptimistic(queryClient, snapshot);
        },
        onSuccess: (result) => {
            if (result.success && result.data) {
                queryClient.setQueryData(
                    onboardingKeys.application(result.data.id),
                    result
                );
                queryClient.setQueryData(
                    onboardingKeys.userApplication(result.data.firebaseUid),
                    result
                );
            }
        }
    });
}

/**
 * Update application mutation. Optimistic — see `useSaveDraft`.
 */
export function useUpdateApplication() {
    const queryClient = useQueryClient();
    const service = getOnboardingService();

    return useMutation<
        RepositoryResult<IOnboardingApplication>,
        Error,
        { id: string } & UpdateApplicationInput,
        ApplicationCacheSnapshot
    >({
        mutationFn: async ({ id, ...input }) => {
            logger.debug('Updating application', { id });
            return service.updateApplication(id, input);
        },
        onMutate: ({ id, applicationData, currentStep }) =>
            applyOptimistic(queryClient, id, (current) => ({
                ...current,
                ...(applicationData !== undefined && {
                    applicationData: {
                        ...current.applicationData,
                        ...applicationData,
                    } as IMerchant,
                }),
                ...(currentStep !== undefined && { currentStep }),
                updatedAt: new Date().toISOString(),
            })),
        onError: (_err, _vars, snapshot) => {
            rollbackOptimistic(queryClient, snapshot);
        },
        onSuccess: (result) => {
            if (result.success && result.data) {
                queryClient.setQueryData(
                    onboardingKeys.application(result.data.id),
                    result
                );
                queryClient.setQueryData(
                    onboardingKeys.userApplication(result.data.firebaseUid),
                    result
                );
            }
        }
    });
}

/**
 * Navigate to next step. Optimistic — see `useSaveDraft`.
 *
 * Note: the caller in `OnboardingFlow` guards against invoking this on
 * the final step (it calls `submit` instead), so unbounded `+ 1` here
 * can't walk past the last step under normal flow. A server rejection
 * would still roll back via `onError`.
 */
export function useNextStep() {
    const queryClient = useQueryClient();
    const service = getOnboardingService();

    return useMutation<
        RepositoryResult<IOnboardingApplication>,
        Error,
        string,
        ApplicationCacheSnapshot
    >({
        mutationFn: async (id) => {
            logger.debug('Moving to next step', { id });
            return service.nextStep(id);
        },
        onMutate: (id) =>
            applyOptimistic(queryClient, id, (current) => ({
                ...current,
                currentStep: current.currentStep + 1,
                updatedAt: new Date().toISOString(),
            })),
        onError: (_err, _vars, snapshot) => {
            rollbackOptimistic(queryClient, snapshot);
        },
        onSuccess: (result) => {
            if (result.success && result.data) {
                queryClient.setQueryData(
                    onboardingKeys.application(result.data.id),
                    result
                );
                queryClient.setQueryData(
                    onboardingKeys.userApplication(result.data.firebaseUid),
                    result
                );
            }
        }
    });
}

/**
 * Navigate to previous step. Optimistic — see `useSaveDraft`.
 * Clamped at 0 to avoid rendering a negative step index during the
 * optimistic window even if the caller somehow bypasses the `isFirst`
 * guard in the UI.
 */
export function usePreviousStep() {
    const queryClient = useQueryClient();
    const service = getOnboardingService();

    return useMutation<
        RepositoryResult<IOnboardingApplication>,
        Error,
        string,
        ApplicationCacheSnapshot
    >({
        mutationFn: async (id) => {
            logger.debug('Moving to previous step', { id });
            return service.previousStep(id);
        },
        onMutate: (id) =>
            applyOptimistic(queryClient, id, (current) => ({
                ...current,
                currentStep: Math.max(0, current.currentStep - 1),
                updatedAt: new Date().toISOString(),
            })),
        onError: (_err, _vars, snapshot) => {
            rollbackOptimistic(queryClient, snapshot);
        },
        onSuccess: (result) => {
            if (result.success && result.data) {
                queryClient.setQueryData(
                    onboardingKeys.application(result.data.id),
                    result
                );
                queryClient.setQueryData(
                    onboardingKeys.userApplication(result.data.firebaseUid),
                    result
                );
            }
        }
    });
}

/**
 * Go to specific step. Optimistic — see `useSaveDraft`.
 */
export function useGoToStep() {
    const queryClient = useQueryClient();
    const service = getOnboardingService();

    return useMutation<
        RepositoryResult<IOnboardingApplication>,
        Error,
        { id: string; step: number },
        ApplicationCacheSnapshot
    >({
        mutationFn: async ({ id, step }) => {
            logger.debug('Going to step', { id, step });
            return service.goToStep(id, step);
        },
        onMutate: ({ id, step }) =>
            applyOptimistic(queryClient, id, (current) => ({
                ...current,
                currentStep: step,
                updatedAt: new Date().toISOString(),
            })),
        onError: (_err, _vars, snapshot) => {
            rollbackOptimistic(queryClient, snapshot);
        },
        onSuccess: (result) => {
            if (result.success && result.data) {
                queryClient.setQueryData(
                    onboardingKeys.application(result.data.id),
                    result
                );
                queryClient.setQueryData(
                    onboardingKeys.userApplication(result.data.firebaseUid),
                    result
                );
            }
        }
    });
}

/**
 * Submit a client-onboarding application.
 *
 * Server-side `submit()` is synchronous (W6): it validates, marks the row
 * `submitted`, then immediately runs `approve()` → `provision()` and
 * returns the provisioned application. By the time this mutation
 * resolves, the user's Firestore profile is already flipped to
 * `isActive: true, onboardingStatus: 'completed'`, so any open auth-app
 * tabs need their `users/{uid}` query refreshed to bounce the user to
 * the management app via `<ManagementRedirect />`.
 */
export function useSubmitApplication() {
    const queryClient = useQueryClient();
    const service = getOnboardingService();

    return useMutation({
        mutationFn: async (id: string) => {
            logger.debug('Submitting application', { id });
            return service.submitApplication(id);
        },
        onSuccess: (result) => {
            if (result.success && result.data) {
                queryClient.setQueryData(
                    onboardingKeys.application(result.data.id),
                    result
                );
                queryClient.setQueryData(
                    onboardingKeys.userApplication(result.data.firebaseUid),
                    result
                );
                // The auth app reads users/{uid} via useGetUserByUid; the
                // tenant-side useAuth() reads authKeys.user(). Both are now
                // stale because the server flipped isActive/onboardingStatus
                // mid-call. Invalidate so any open tab (including this one,
                // if the user came in through auth) re-fetches and routes
                // to the management redirect.
                queryClient.invalidateQueries({
                    queryKey: userKeys.detailByUid(result.data.firebaseUid),
                });
                queryClient.invalidateQueries({ queryKey: authKeys.user() });
            }
        }
    });
}

// ============================================================================
// Subscription Hooks (no-ops — polling in useApplication handles freshness)
// ============================================================================
//
// The old implementation opened Supabase postgres_changes WebSockets. Now
// that all data access flows through the API, `useApplication` polls every
// 5s while status is pending underwriting review. These hooks are kept as
// no-ops so callers that imported them still compile, but they do nothing.
// Safe to delete when no external code references them.

/** @deprecated No-op. Freshness is handled by `useApplication` polling. */
export function useApplicationSubscription(_id: string | undefined): void {
    // Intentionally empty — see note above.
}

/** @deprecated No-op. Freshness is handled by `useApplication` polling. */
export function useUserApplicationSubscription(_firebaseUid: string | undefined): void {
    // Intentionally empty — see note above.
}

// ============================================================================
// Composite Hook
// ============================================================================

/**
 * Composite hook for application state and actions
 *
 * Provides all necessary state and actions for managing an onboarding application.
 */
export function useOnboardingApplication(firebaseUid: string | undefined) {
    const applicationQuery = useApplication(firebaseUid);
    const createOrGetMutation = useCreateOrGetApplication();
    const saveDraftMutation = useSaveDraft();
    const nextStepMutation = useNextStep();
    const previousStepMutation = usePreviousStep();
    const goToStepMutation = useGoToStep();
    const submitMutation = useSubmitApplication();

    // Freshness is handled by `useApplication` polling while status is
    // pending underwriting — no manual subscription needed.
    const result = applicationQuery.data;
    const application = result?.success ? result.data ?? undefined : undefined;

    // The service never THROWS on a failed lookup — it resolves
    // `{ success: false, error }` — so React Query reports a SUCCESSFUL query
    // and `isError` stays false. Reading only `isError` therefore left a failed
    // `/onboarding/me` (or an answer with no application in it) as "not
    // loading, no error, no application": the page held its skeleton forever.
    // A settled lookup that produced no application IS the error state.
    const lookupFailed = result !== undefined && !application;

    return {
        // State
        application,
        isLoading: applicationQuery.isLoading,
        isError: applicationQuery.isError || lookupFailed,
        error:
            result?.error ||
            applicationQuery.error?.message ||
            (lookupFailed ? 'Your application could not be loaded.' : undefined),

        // Actions
        createOrGetApplication: createOrGetMutation.mutateAsync,
        isCreating: createOrGetMutation.isPending,

        saveDraft: async (data: Partial<IMerchant>, step: number) => {
            if (!application?.id) return;
            return saveDraftMutation.mutateAsync({ id: application.id, data, step });
        },
        isSaving: saveDraftMutation.isPending,

        nextStep: async () => {
            if (!application?.id) return;
            return nextStepMutation.mutateAsync(application.id);
        },

        previousStep: async () => {
            if (!application?.id) return;
            return previousStepMutation.mutateAsync(application.id);
        },

        goToStep: async (step: number) => {
            if (!application?.id) return;
            return goToStepMutation.mutateAsync({ id: application.id, step });
        },

        submit: async () => {
            if (!application?.id) return;
            return submitMutation.mutateAsync(application.id);
        },
        isSubmitting: submitMutation.isPending,

        // Query actions
        refetch: applicationQuery.refetch
    };
}
